"use client";

import { RiAppleFill, RiCalendarEventLine, RiHeartFill, RiLiveLine, RiSoundcloudFill } from "@remixicon/react";
import { formatTimeRangeWithPreferences } from "@workspace/shared-utils";
import type { ProcessedSet, Review } from "@workspace/types";
import { isProgramSet } from "@workspace/types";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@workspace/ui/components/tooltip";
import { cn } from "@workspace/ui/lib/utils";
import { type MouseEvent, memo, useCallback } from "react";
import { useSetSocialInteractions } from "@/hooks/shared";
import { useCalendarPreferences } from "@/hooks/shared/use-calendar-preferences";
import { useUserPreferences } from "@/hooks/shared/use-user-preferences";
import {
	getSetSizeCategory,
	shouldShowTextForShortSet,
	shouldShowTimeForShortSet,
} from "@/lib/features/festival/calendar/calendar-dimensions";

// Helper to determine which recording icon to show based on available URLs
const getRecordingIcon = (set: ProcessedSet) => {
	const hasYoutube = !!set.youtube_url;
	const hasSoundcloud = !!set.soundcloud_url;
	const hasAppleMusic = !!set.apple_music_url;

	// Count how many recording sources are available
	const sourceCount = [hasYoutube, hasSoundcloud, hasAppleMusic].filter(Boolean).length;

	// If multiple sources or YouTube is present, use the live/camera icon
	if (sourceCount > 1 || hasYoutube) {
		return RiLiveLine;
	}

	// Single source - show specific icon
	if (hasSoundcloud) {
		return RiSoundcloudFill;
	}
	if (hasAppleMusic) {
		return RiAppleFill;
	}

	return null;
};
import { ArtistPhoto } from "@/components/shared/photos/artist-photo";
import {
	getSetButtonClassName,
	getSetButtonContent,
	getSetButtonTitle,
	getSetCardActiveClassName,
	getSetCardClassName,
	getSetCardHoverClassName,
	getSetStatus,
	renderArtistName,
	renderFacepile,
	renderStarRating,
} from "@/lib/utils/set-card-helpers";
import { useCurrentUser } from "@/stores";

interface CalendarSetCardProps {
	set: ProcessedSet;
	review: Review | undefined;
	savedSchedule: string[];
	festivalSlug: string;
	variant?: "personal" | "public";
	onStartReview?: (setId: string) => void;
	onToggleSetInSchedule?: (setId: string) => void;
	topOffset: string;
	setHeight: string;
	readOnly?: boolean;
	onAuthRequired?: () => void;
	isHorizontal?: boolean;
}

export const CalendarSetCard = memo(function CalendarSetCard({
	set,
	review,
	savedSchedule,
	festivalSlug,
	variant = "public",
	onStartReview = () => {},
	onToggleSetInSchedule = () => {},
	topOffset,
	setHeight,
	readOnly = false,
	onAuthRequired,
	isHorizontal = false,
}: CalendarSetCardProps) {
	// Read artist photo preference directly from hook (matches mobile pattern)
	const { preferences: calendarPreferences } = useCalendarPreferences();
	const showArtistPhotos = calendarPreferences?.show_artist_photos ?? true;

	// Use TanStack Query hook for social interactions
	const currentUser = useCurrentUser();
	const { socialInteractions, isLoading: isSocialInteractionsLoading } =
		useSetSocialInteractions(festivalSlug, currentUser?.id || null, set.id);

	// Get user preferences for time formatting
	const userPreferences = useUserPreferences();

	const rating = review?.rating;
	const isLoved = review?.loved;

	// Use shared status utilities
	const { status } = getSetStatus(review);

	// Memoize event handlers
	const handleStartReview = useCallback(() => {
		onStartReview(set.id);
	}, [onStartReview, set.id]);

	const handleToggleSetInSchedule = useCallback(
		(e: MouseEvent) => {
			e.stopPropagation();
			onToggleSetInSchedule(set.id);
		},
		[onToggleSetInSchedule, set.id],
	);

	// Handle auth-required click for non-authenticated users
	const handleAuthRequiredClick = useCallback(
		(e: MouseEvent) => {
			e.stopPropagation();
			onAuthRequired?.();
		},
		[onAuthRequired],
	);

	// Get the size category for this set based on duration
	const sizeCategory = getSetSizeCategory(set.durationHours);

	// Determine display mode based on size and layout
	const shouldShowText = shouldShowTextForShortSet(sizeCategory);
	const shouldShowTime = shouldShowTimeForShortSet(sizeCategory);
	const shouldShowFullContent = sizeCategory === "normal" || shouldShowText;
	const shouldShowMinimalContent = !shouldShowFullContent;

	// Get padding based on size category
	const getPaddingClassName = () => {
		switch (sizeCategory) {
			case "10m-short":
				return "px-1 py-1";
			case "15m-short":
				return "px-1.5 py-1.5";
			case "20m-short":
				return "px-2 py-1.5";
			case "25m-short":
				return "px-2 py-2";
			case "30m-short":
				return "px-2 py-2";
			default:
				return "px-2 py-2";
		}
	};

	// Get minimum height based on size category (proportional to actual duration)
	const getMinHeight = () => {
		switch (sizeCategory) {
			case "10m-short":
				return "27px"; // Math.max(24, 27px) = 27px
			case "15m-short":
				return "40px"; // 40px (proportional)
			case "20m-short":
				return "53px"; // 53px (proportional)
			case "25m-short":
				return "67px"; // 67px (proportional)
			case "30m-short":
				return "80px"; // 80px (proportional)
			default:
				return "80px";
		}
	};

	// Helper function to get preference-aware time formatting
	const getPreferenceAwareTime = () => {
		if (set.start_time && set.end_time) {
			return formatTimeRangeWithPreferences(
				set.start_time,
				set.end_time,
				userPreferences,
			);
		}
		return set.time;
	};

	// Helper function to extract and format just the start time with dash for horizontal short sets
	const getShortTimeFormat = (timeRange: string) => {
		// Handle different time formats: "2:00AM-2:30AM" or "2:00AM - 2:30AM"
		const startTime = timeRange.split(/\s*-\s*/)[0]?.trim();
		return `${startTime}-`;
	};

	return (
		<div
			key={set.id} // Keep key here for mapping
			className={cn(
				"group absolute inset-x-0 flex flex-col justify-between overflow-hidden rounded-lg text-sm",
				getPaddingClassName(),
				getSetCardClassName(status, "calendar"),
				getSetCardHoverClassName(status),
				getSetCardActiveClassName(status),
				"transition-all duration-150",
				"dark:hover:border-border/60",
				"cursor-pointer",
				"active:scale-[0.99]",
				"h-full w-full", // Added to ensure proper sizing in both layouts
			)}
			style={{
				top: topOffset,
				height: setHeight,
				minHeight: getMinHeight(),
				zIndex: 1,
			}}
			onClick={handleStartReview} // Open review modal on click
		>
			{/* Content based on size category */}
			<div>
				{shouldShowMinimalContent ? (
					<div className="absolute inset-0 flex h-full w-full items-center justify-center">
						<RiCalendarEventLine
							className={cn(
								"text-foreground",
								sizeCategory === "10m-short" ? "h-3 w-3" : "h-4 w-4",
							)}
						/>
					</div>
				) : (
					<>
						{/* Photo + Name/Time row */}
						<div className="flex items-start gap-1.5">
							{(sizeCategory === "normal" ||
								sizeCategory === "30m-short" ||
								sizeCategory === "25m-short") &&
								showArtistPhotos && (
								<ArtistPhoto
									name={set.artist?.name || "Artist"}
									imageUrl={set.is_surprise ? null : set.artist?.image_url}
									size="sm"
									rounded="card"
									isSurprise={!!set.is_surprise}
									isProgram={isProgramSet(set)}
									className="flex-shrink-0 !rounded-md"
								/>
							)}

							<div className="min-w-0 flex-1">
								{/* Artist Name */}
								<TooltipProvider>
									<Tooltip>
										<TooltipTrigger asChild>
											<div
												className={cn(
													"font-semibold text-xs leading-tight",
													isHorizontal
														? "truncate"
														: shouldShowText && !shouldShowTime
															? "truncate"
															: "line-clamp-2",
												)}
											>
												{renderArtistName({
													set,
													truncateMode: "none",
													textBreaking: isHorizontal
														? "whitespace-nowrap"
														: "break-words",
													className: "font-semibold leading-tight text-xs",
												})}
											</div>
										</TooltipTrigger>
										<TooltipContent className="border border-border bg-popover text-popover-foreground">
											<span>
												{renderArtistName({
													set,
													truncateMode: "none",
													textBreaking: "break-words",
												})}
											</span>
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>

								{/* Time - show for 25m/30m short sets and normal sets */}
								{(shouldShowTime || sizeCategory === "normal") && (
									<div
										className={cn(
											"flex items-start gap-1 text-foreground/50 text-xs leading-tight",
										)}
									>
										{(() => {
											const RecordingIcon = getRecordingIcon(set);

											// Only apply special short set logic for horizontal calendar
											if (isHorizontal) {
												// Determine set duration category for time display logic
												const durationMinutes = set.durationHours * 60;
												const isShortSet = durationMinutes <= 30;
												const is25mOr30mSet =
													durationMinutes >= 25 && durationMinutes <= 30;

												if (is25mOr30mSet) {
													// For 25m and 30m sets: show shortened time format with recording icon if present
													const preferenceAwareTime = getPreferenceAwareTime();
													return (
														<>
															<span>{getShortTimeFormat(preferenceAwareTime)}</span>
															{RecordingIcon && (
																<RecordingIcon className="h-3 w-3 text-foreground/50" />
															)}
														</>
													);
												}
												if (isShortSet && RecordingIcon) {
													// For other short sets with recording: show recording icon instead of time
													return (
														<RecordingIcon className="h-3 w-3 text-foreground/50" />
													);
												}
											}

											// Default: show time and recording icon if present (for vertical calendar and longer horizontal sets)
											return (
												<>
													<span className="line-clamp-2">{getPreferenceAwareTime()}</span>
													{RecordingIcon && (
														<RecordingIcon className="h-3 w-3 flex-shrink-0 text-foreground/50" />
													)}
												</>
											);
										})()}
									</div>
								)}
								{/* Rating Stars and Social Interactions - show for 25m/30m short sets and normal sets */}
								{(sizeCategory === "normal" ||
									sizeCategory === "25m-short" ||
									sizeCategory === "30m-short") && (
									<div className="flex items-center">
										{rating && rating > 0 ? (
											<>
												{renderStarRating({ rating, size: "sm" })}
												{/* Heart if loved */}
												{isLoved && (
													<RiHeartFill className="ml-0.5 h-3 w-3 text-red-500" />
												)}
											</>
										) : isLoved ? (
											<RiHeartFill className="h-3 w-3 text-red-500" />
										) : (
											renderFacepile({
												socialInteractions,
												set,
												festivalSlug,
												variant,
												isLoading: isSocialInteractionsLoading,
												size: "sm",
											}) || <div className="h-3 w-3" />
										)}
									</div>
								)}

								{/* Social interactions facepile - only show if we have ratings/loved */}
								{(sizeCategory === "normal" ||
									sizeCategory === "25m-short" ||
									sizeCategory === "30m-short") &&
									((rating && rating > 0) || isLoved) && (
										<div>
											{renderFacepile({
												socialInteractions,
												set,
												festivalSlug,
												variant,
												isLoading: isSocialInteractionsLoading,
												size: "sm",
											})}
										</div>
									)}
							</div>
						</div>
					</>
				)}
			</div>

			{/* Bottom part: List Button Only - Show for normal sets, or 25m+ sets in horizontal layout */}
			{(sizeCategory === "normal" ||
				(isHorizontal &&
					(sizeCategory === "25m-short" || sizeCategory === "30m-short"))) && (
				<div>
					{(() => {
						// True read-only mode (viewing other user's profile)
						if (readOnly && !onAuthRequired) {
							// Only show status indicators, never interactive buttons
							const hasStatus =
								savedSchedule.includes(set.id) ||
								review?.sawLive ||
								review?.sawStream ||
								review?.mustSee ||
								(review?.rating && review.rating > 0);

							if (!hasStatus) return null; // Don't show "Add to List" for other users

							return (
								<div
									className={cn(
										"flex w-full min-w-0 items-center justify-center gap-1 whitespace-nowrap rounded-md border py-0.5 text-center text-xs",
										getSetButtonClassName(status).replace(
											/hover:[^ ]+ |active:[^ ]+ /g,
											"",
										),
									)}
								>
									{getSetButtonContent(status, set)}
								</div>
							);
						}

						// Non-auth user on public page - show button that opens auth modal
						if (readOnly && onAuthRequired) {
							return (
								<button
									type="button"
									className={cn(
										"flex w-full min-w-0 items-center justify-center gap-1 whitespace-nowrap rounded-md border py-0.5 text-center text-xs transition-colors active:scale-[0.99]",
										getSetButtonClassName("default"),
									)}
									onClick={handleAuthRequiredClick}
									title="Sign in to add to your list"
								>
									{getSetButtonContent("default", set)}
								</button>
							);
						}

						// Authenticated user - normal interactive button
						return (
							<button
								type="button"
								className={cn(
									"flex w-full min-w-0 items-center justify-center gap-1 whitespace-nowrap rounded-md border py-0.5 text-center text-xs transition-colors active:scale-[0.99]",
									getSetButtonClassName(status),
								)}
								onClick={handleToggleSetInSchedule}
								title={getSetButtonTitle(status, set)}
							>
								{getSetButtonContent(status, set)}
							</button>
						);
					})()}
				</div>
			)}
		</div>
	);
});

CalendarSetCard.displayName = "CalendarSetCard";
